import React, { createContext, useCallback, ReactNode, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { useUser, useClerk, useAuth as useClerkAuth } from '@clerk/nextjs';
import { api } from '@/lib/convex';
import { setSentryUser, clearSentryUser } from '@/utils/sentry';
import type { User, UserRole, AuthContextType } from '@/contexts/auth-types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isLoaded: clerkLoaded, isSignedIn } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();

  const isAuthenticated = Boolean(isSignedIn && clerkLoaded);

  // Fetch profile from Convex; the /clerk-webhook handler syncs the row in
  // on user.created. If it's missing briefly after signup, profile === null
  // until the webhook fires — the UI treats that like isLoading.
  const profile = useQuery(
    api.users.getMyProfile,
    isAuthenticated ? {} : 'skip'
  );

  const user: User | null = useMemo(() => {
    if (!isAuthenticated || !profile) return null;

    const mapped: User = {
      id: profile._id,
      email: profile.email ?? clerkUser?.primaryEmailAddress?.emailAddress ?? '',
      name: profile.displayName ?? profile.username ?? clerkUser?.fullName ?? 'User',
      role: (profile.role as UserRole) ?? 'user',
      isVerified: profile.verificationLevel !== 'none' && !!profile.verificationLevel,
      verificationLevel: (profile.verificationLevel ?? 'none') as User['verificationLevel'],
      avatar: profile.profileImageUrl ?? clerkUser?.imageUrl ?? undefined,
      status: profile.status as User['status'],
    };

    setSentryUser({ id: mapped.id, email: mapped.email, username: profile.username ?? undefined });
    return mapped;
  }, [isAuthenticated, profile, clerkUser]);

  const prevAuthenticated = React.useRef(isAuthenticated);
  React.useEffect(() => {
    if (prevAuthenticated.current && !isAuthenticated) {
      clearSentryUser();
    }
    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  const isLoading = !clerkLoaded || (isAuthenticated && profile === undefined);

  const logout = useCallback(async () => {
    clearSentryUser();
    // Tell the service worker (if any) to wipe identity-specific
    // caches so the next user on this device doesn't see the previous
    // user's marketing/profile pages. SECURITY_AUDIT.md H7.
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_AUTH_CACHES' });
      } catch {
        // postMessage failures are non-fatal — keep signing out.
      }
    }
    await signOut();
  }, [signOut]);

  const updateProfileMutation = useMutation(api.users.updateProfile);

  const updateUserProfile = useCallback(async (updates: Partial<User>) => {
    const patch: Record<string, unknown> = {};
    if (updates.name !== undefined) patch.displayName = updates.name;
    if (updates.avatar !== undefined) patch.profileImageUrl = updates.avatar;

    if (Object.keys(patch).length > 0) {
      await updateProfileMutation(patch as Parameters<typeof updateProfileMutation>[0]);
    }
  }, [updateProfileMutation]);

  const refreshSession = useCallback(async () => {
    // Clerk handles session refresh automatically.
  }, []);

  const refreshUserProfile = useCallback(async () => {
    // Convex useQuery auto-refreshes.
  }, []);

  // Legacy auth methods — pages that still call these need migration to
  // Clerk hooks. Throwing makes any leftover consumer fail loud instead of
  // silently breaking. Remove once every caller is gone.
  const notMigrated = useCallback(async (): Promise<any> => {
    throw new Error(
      'Legacy auth method is no longer wired. Call Clerk hooks (useSignIn / useSignUp / useClerk) from the auth page directly.'
    );
  }, []);

  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    isAdmin: user?.role === 'admin',
    isLoading,
    isPaused: user?.status === 'paused',
    isBanned: user?.status === 'banned',
    isDeactivating: user?.status === 'deactivating',
    isDisabled: user?.status === 'disabled',
    logout,
    updateUserProfile,
    refreshSession,
    refreshUserProfile,
    login: notMigrated,
    signup: notMigrated,
    verifyEmail: notMigrated,
    resetPassword: notMigrated,
  }), [user, isAuthenticated, isLoading, logout, updateUserProfile, refreshSession, refreshUserProfile, notMigrated]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
